<script>
import { commands } from '../../js/lib/functions.js'
import { listenToCurrentUserView } from '../../js/readModel.js';
import { hideLoading, showLoading } from '../../js/utils/loading.js';

export const pageName = 'account-dbs';
export const pageRoute = '/account/dbs';
export const title = 'D.B.S.';

export default {
	data() {
		return { 
			user: null,
			availability: null,
			ready: false
		}
	},
	methods: {
		beforeHide() {
			return this.isDirty ? 'Are you sure you want to abandon your changes?' : null
		},
		async boot(opts) {
			await listenToCurrentUserView(user => this.user = user);
			this.ready = true;
		},
		async submit()
		{
			commands.warmUp();

			showLoading();

			await commands.users.dbs.begin({ availability: this.availability});

			hideLoading();
		}
	}
}
</script>
<template>
<div class="container" v-if="ready">
	<div class="row justify-content-center">
		<div class="col-12 col-lg-8 mt-5">
			<h1 class="display-4 text-center mb-3">Disclosure and Barring Service</h1>
			<p class="text-muted text-center mb-2">Certain roles within UKRA will soon require a DBS check.</p>
			<p class="text-muted text-center mb-5">Here you can check your requirement and DBS status.</p>
			
			<div v-if="user.dbsSummary.required && user.dbsSummary.status == 'none'" class="alert alert-danger">
				<h4>You do not have a valid DBS Check</h4>
				<p class="mb-0">You require a valid DBS check and you do not have one. Get started!</p>
			</div>
			<div v-if="user.dbsSummary.required && user.dbsSummary.status != 'none' && user.dbsSummary.status != 'passed'" class="alert alert-warning">
				<h4>You have not completed your DBS Check</h4>
				<p class="mb-0">You require a valid DBS check and you do not have one.</p>
			</div>

			<div v-if="user.dbsSummary.required" class="card mb-0">
				<div class="card-body">
					<p>You personally require a DBS check because you either perform or have applied for the following roles:</p>
					
					<div class="list-group list-group-flush mb-n3">
						<div v-if="user.dbsSummary.rso" class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div class="avatar-title fs-lg bg-light rounded-circle text-primary">
											<i class="fe fe-shield"></i>
										</div>
									</div>
								</div>
								<div class="col ms-n2">
									<h5 class="mb-1">RSO</h5>
									<p class="small text-gray-700 mb-0">Range Safety Officer</p>
								</div>
							</div>
						</div>
						<div v-if="user.dbsSummary.mapa" class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div class="avatar-title fs-lg bg-light rounded-circle text-primary">
											<i class="fe fe-check-square"></i>
										</div>
									</div>
								</div>
								<div class="col ms-n2">
									<h5 class="mb-1">MAPA</h5>
									<p class="small text-gray-700 mb-0">MAP Assessor</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="card mb-0">
				<div class="card-body">
					<p class="mb-0 text-center">You do not require a DBS check to participate in UKRA.</p>
				</div>
			</div>
		</div>
	</div>
	
	<hr class="my-5" v-if="user.dbsSummary.required" />

	<div v-if="user.dbsSummary.required" class="row">
		<div class="col col-12 col-lg-8">
			<div v-if="user.dbsSummary.status == 'none'" class="card">
				<div class="card-header">
					<h4 class="card-header-title">Start application</h4>
				</div>
				<div class="card-body">
					<form ref="form" @submit.prevent="submit">
							
						<div class="form-group">
							<label class="form-label mb-1">Availability</label>
							<textarea class="form-control" v-model="availability" required rows="4"></textarea>
							<span class="invalid-feedback"></span>
							<p class="form-text text-muted small mb-0">Provide as many dates and times as possible that you can be available to meet physically with UKRA and provide ID. Ideally this will be at a UKRA affiliated club launch, unless you're willing to travel.</p>
						</div>
							
						<button class="btn btn-primary btn-block">Apply for a DBS check <span class="fe fe-arrow-right-circle"></span></button>
					</form>
				</div>
			</div>
			<div v-if="user.dbsSummary.status == 'requested'" class="card">
				<div class="card-header">
					<h4 class="card-header-title">Complete application form</h4>
				</div>
				<div class="card-body">
					<p>You need this application code: <strong class="selectable">STENCP35586</strong></p>
					<p>If you have already completed this please be patient whilst we process it and get back to you.</p>
					<a href="https://form.crbs.info/Account/Login?branch=United_Kingdom_Rocketry_Association" target="_blank" class="btn btn-primary btn-block">Complete application form <span class="fe fe-external-link"></span></a>
				</div>
			</div>
			<div v-if="user.dbsSummary.status == 'received'" class="card">
				<div class="card-header">
					<h4 class="card-header-title">Verify identity</h4>
				</div>
				<div class="card-body">
					<p>You must physically meet with UKRA and provide your identity documents.</p>
					<p>We will be in touch shortly to arrange the best time and place to meet you.</p>
					<a href="https://ukra.org.uk/info/legal/dbs-identity-verification/" target="_blank">View required documents <span class="fe fe-external-link"></span></a>
				</div>
			</div>
			<div v-if="user.dbsSummary.status == 'verified'" class="card">
				<div class="card-header">
					<h4 class="card-header-title">Pass DBS check</h4>
				</div>
				<div class="card-body">
					<p>Your DBS check is in progress and will be completed in a few weeks.</p>
					<p>You will receive your DBS certificate in the mail.</p>
					<p>We only get informed if your certificate has content of note or not, we do not automatically get to see the contents.</p>
					<p class="mb-0">In the case that your certificate is not completely clear, we will need to discuss with you the nature of the contents and determine the best course of action. Otherwise your DBS check will be automatically passed and you are ready to go.</p>
				</div>
			</div>
			<div v-if="user.dbsSummary.status == 'passed'" class="card">
				<div class="card-header">
					<h4 class="card-header-title">DBS check passed</h4>
				</div>
				<div class="card-body">
					<p>You have passed your DBS check.</p>
					<p class="mb-0">You are required to reconfirm aspects of the DBS check every year. We will contact you when this is necessary.</p>
				</div>
			</div>
		</div>
		<div v-if="user.dbsSummary.required" class="col col-12 col-lg-4">
			<div class="card">
				<div class="card-header">
					<h4 class="card-header-title">Progress</h4>
				</div>
				<div class="card-body">
					<div class="list-group list-group-flush list-group-activity my-n3">
						<div class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div :class="`avatar-title fs-lg rounded-circle ${user.dbsSummary.status == 'none' ? 'text-white bg-primary' : 'text-white bg-success'}`">
											<i class="fe fe-play"></i>
										</div>
									</div>
								</div>
								<div class="col mb-n2">
									<h5 class="mb-1">Start application</h5>
									<p class="small text-gray-700 mb-0">Apply for a DBS check</p>
									<p class="text-body-secondary mb-0">1 min, online</p>
								</div>
							</div>
						</div>
						<div class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div :class="`avatar-title fs-lg rounded-circle ${user.dbsSummary.status == 'none' ? 'text-secondary bg-light' : user.dbsSummary.status == 'requested' ? 'text-white bg-primary' : 'text-white bg-success'}`">
											<i class="fe fe-send"></i>
										</div>
									</div>
								</div>
								<div class="col mb-n2">
									<h5 class="mb-1">Complete application form</h5>
									<p class="small text-gray-700 mb-0">Fill in your personal detail with our 3rd party DBS checking provider</p>
									<p class="text-body-secondary mb-0">5 mins, online</p>									
								</div>
							</div>
						</div>
						<div class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div :class="`avatar-title fs-lg rounded-circle ${user.dbsSummary.status == 'none' || user.dbsSummary.status == 'requested' ? 'text-secondary bg-light' : user.dbsSummary.status == 'received' ? 'text-white bg-primary' : 'text-white bg-success'}`">
											<i class="fe fe-user-check"></i>
										</div>
									</div>
								</div>
								<div class="col mb-n2">
									<h5 class="mb-1">Verify identity</h5>
									<p class="small text-gray-700 mb-0">Provide UKRA with identification documents</p>
									<p class="text-body-secondary mb-0">15 mins, in person</p>
								</div>
							</div>
						</div>
						<div class="list-group-item">
							<div class="row">
								<div class="col-auto">
									<div class="avatar avatar-sm">
										<div :class="`avatar-title fs-lg rounded-circle ${user.dbsSummary.status == 'none' || user.dbsSummary.status == 'requested' || user.dbsSummary.status == 'received' ? 'text-secondary bg-light' : user.dbsSummary.status == 'verified' ? 'text-primary' : 'text-white bg-success'}`">
											<i class="fe fe-check-circle"></i>
										</div>
									</div>
								</div>
								<div class="col ms-n2">
									<h5 class="mb-1">Pass DBS Check</h5>
									<p class="small text-gray-700 mb-0">Your details are checked against government databases</p>
									<small class="text-body-secondary">1-2 weeks</small>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
</template>